import store from '@/store/store';
import eventbus from 'tiny-emitter/instance';
import first from 'lodash/first';
import { TEXT, TYPE, GA, SECTION, RENDERER_MODE, SECTIONSTATUS } from '@/constants';
import { Section } from './section';
import Matrix from '@/core/Matrix';
import SVG from 'svg.js';
import { lastUsedSectionFontSize } from '@/core/utils';
import { createPathEditTool } from '@/core/tools/PathTool';
import { ModelParams } from '../model';
import { useActions } from '@/store/useStore';
import { preloadThumbnail } from '@/core/drawing/utils';

export default class PathSection extends Section {
  declare shape: SVG.Path;

  finishEdit = () => {};

  constructor(params: ModelParams) {
    super(params);
    this.initView();
  }

  // @ts-expect-error
  get subtype() {
    return TYPE.PATH;
  }

  plot(onResizing = false) {
    const { path } = this.attributes;

    this.shape = this.view.path(path);
    this.attributes.points = this.shape.array().valueOf();
  }

  edit() {
    this.backupShape();
    const { disableSelection } = useActions('settings');
    this.app.seatmap.selection.purge();
    eventbus.emit('selection:updated', this.app.seatmap.selection);
    disableSelection();
    this.finishEdit = createPathEditTool(this.app, this).finish;
    this.app.seatmap.actions.setResizedUnit(this.uuid);
  }

  initView() {
    const { transform, shapetransform, scale } = this.attributes;
    this.plot();
    this.view.addClass(`section-shape section-${this.subtype}`);
    this.view.transform(transform);
    this.shape.transform(shapetransform);

    if (!RENDERER_MODE) {
      this.shape.attr({ stroke: SECTION.DEFAULTSTROKECOLOR });
    }
    this.initLabel();
    if (this.attributes.color) {
      this.changeFillColor(this.attributes.color, false);
      this.changeLabelStroke(this.attributes.label.stroke, false);
    }
    if (this.attributes.opacity !== undefined) {
      this.changeOpacity(this.attributes.opacity, false);
    }
    if (this.categoryId) {
      this.applyCategoryColor(this.app.getCategoryColor(this.categoryId));
    }
    const lthis = this;
    let hoverTime: number;

    const onClick = function (evt: MouseEvent) {
      if (
        RENDERER_MODE &&
        !lthis.isInside &&
        !store.state.seatmap.isPanning &&
        store.state.seatmap.isClickable &&
        !lthis.attributes.ga &&
        !lthis.category?.bestAvailable &&
        lthis.status === SECTIONSTATUS.AVAILABLE
      ) {
        let point = (lthis.svg.node as unknown as SVGSVGElement).createSVGPoint();
        point.x = evt.clientX;
        point.y = evt.clientY;
        point = point.matrixTransform((lthis.svg.node as unknown as SVGSVGElement).getScreenCTM()!.inverse());
        lthis.app.currentSection = lthis;
      }

      if (
        RENDERER_MODE &&
        !lthis.isInside &&
        !lthis.attributes.ga &&
        !lthis.category?.bestAvailable &&
        lthis.status === SECTIONSTATUS.AVAILABLE
      ) {
        lthis.app.zoomManager.zoomAndCenterBox(lthis.view.rbox(lthis.app.canvas));
      }

      if (RENDERER_MODE && !lthis.isInside && lthis.app.props.onSectionClick) {
        lthis.app.props.onSectionClick(lthis, lthis.category?.bestAvailable === true);
      }

      if (
        RENDERER_MODE &&
        (lthis.category?.bestAvailable || lthis.attributes.ga) &&
        lthis.status === SECTIONSTATUS.AVAILABLE
      ) {
        eventbus.emit('show:sectionAlert', lthis);
      }
    };
    this.shape.on('click', onClick);

    this.shape.mouseover(function (e: MouseEvent) {
      if (!lthis.isInside) {
        if (RENDERER_MODE) {
          preloadThumbnail(lthis.uuid, lthis.app);
          lthis.canvas.attr({ cursor: 'pointer' });
          lthis.addBorder();
          if (!lthis.app.isMobileDevice) {
            hoverTime = setTimeout(() => {
              eventbus.emit('tooltip:show', lthis, e);
            }, 200);
          }
          if (lthis.app.props.onSectionOver) {
            lthis.app.props.onSectionOver(lthis);
          }
        } else {
          lthis.highlight();
        }
      }
    });

    this.shape.on('touchmove', function () {
      eventbus.emit('tooltip:hide', lthis);
    });
    this.shape.mousemove(function (e: MouseEvent) {
      if (RENDERER_MODE && !lthis.isInside && !lthis.app.isMobileDevice) {
        preloadThumbnail(lthis.uuid, lthis.app);
        clearTimeout(hoverTime);
        eventbus.emit('tooltip:hide', lthis, e);
        hoverTime = setTimeout(() => {
          eventbus.emit('tooltip:show', lthis, e);
        }, 200);
      }
    });
    this.shape.mouseout(function (e: MouseEvent) {
      if (!RENDERER_MODE) {
        lthis.unhighlight();
      } else if (RENDERER_MODE) {
        lthis.removeBorder();
        if (lthis.app.props.onSectionOut) {
          lthis.app.props.onSectionOut(lthis);
        }
      }
      clearTimeout(hoverTime);
      eventbus.emit('tooltip:hide', lthis, e);
    });

    if (scale) {
      this.changeScale(scale * 100, 100, true, true);
    }
    //this.updateConflictedZone();
  }

  toJSON() {
    const { uuid, type, subtype, sid } = this;
    const transform = this.view.transform('matrix' as any);
    const shapetransform = this.shape.transform('matrix' as any);
    const labeltransform = this.label.transform('matrix' as any);
    const attributes = Object.assign({}, this.attributes, {
      transform,
      shapetransform,
      labeltransform,
    });
    return PathSection.filteredJSON({
      uuid,
      sid,
      type,
      subtype,
      attributes,
    });
  }

  static fromJSON(params: ModelParams) {
    return PathSection.create(params);
  }

  static create(params: ModelParams) {
    const attributes = {
      label: Object.assign({}, GA.LABEL, {
        text: 'Section',
        size: lastUsedSectionFontSize.value,
        transform: '',
        fill: SECTION.DEFAULTFILLCOLOR,
        stroke: SECTION.DEFAULTSTROKECOLOR,
      }),
      path: '',
      color: SECTION.DEFAULTFILLCOLOR,
      points: [],
      smoothing: 0,
      transform: '',
      shapecenter: [],
      shapetransform: '',
    };
    params.attributes = Object.assign({}, attributes, params.attributes);
    return new PathSection(params);
  }

  bbox() {
    const bbox = this.view.bbox();
    return bbox.transform(
      new Matrix().transform({ rotate: this.attributes.rotation, ox: bbox.cx, oy: bbox.cy })
    ) as SVG.BBox;
  }

  backupShape() {
    const { path, rotation, position } = this.attributes;
    // @ts-expect-error
    const matrix = this.view.transform('matrix');
    this.backup = { path, rotation, position, matrix };
  }

  restoreBackup() {
    if (this.backup) {
      const { path, rotation, position, matrix } = this.backup;
      Object.assign(this.attributes, { path, rotation, position });
      this.shape.plot(path);
      this.view.untransform().transform(matrix);
      this.label.attr({
        cx: position[0],
        cy: position[1],
      });
    }
  }
}
